/**
 * SkyRenderer — physically-based Rayleigh + Mie + ozone sky node.
 *
 * Implements an inline ray-march equivalent of the Hillaire 2020 sky-view
 * LUT computation. Each fragment:
 *
 *   1. Computes the view direction from the camera through the dome.
 *   2. Intersects the ray with the atmosphere shell (top sphere).
 *   3. Marches `numSamples` steps through the shell. At each sample:
 *        - evaluates Rayleigh/Mie density
 *        - ray-marches a "sun visibility" ray from the sample to the top of
 *          the atmosphere toward the sun and accumulates optical depth
 *        - adds in-scattered radiance weighted by phase functions and
 *          cumulative view transmittance
 *   4. Returns the final summed radiance.
 *
 * Complexity is `O(numSamples * sunSamples)` per pixel. The defaults
 * (30 × 8) are a good quality / perf compromise for a fullscreen sky.
 *
 * This node is designed for `scene.backgroundNode` assignment and for
 * rendering into a small cube target (for PMREM IBL). It's not intended
 * for per-pixel use by arbitrary materials — those should read the PMREM
 * environment map or a future aerial-perspective volume.
 *
 * v1 trades the 4-LUT Hillaire pipeline for simplicity. A v2 can port the
 * LUT bakers (transmittance 256×64, multi-scatter 32×32, sky-view 192×108)
 * for ~10× fragment-shader speedup at the cost of compute-pass management.
 * Parameterization helpers for that path are already in `lutParameterization.ts`.
 *
 * Units: kilometres inside the shader; caller converts world meters → km.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, Loop, vec2, vec3, vec4, float, uniform, uint,
  exp, max, sqrt, dot, normalize, mix, step,
  positionWorldDirection, texture,
} from 'three/tsl';
import type { AtmosphereConfig } from './AtmosphereConfig.js';
import {
  atmosphereDensities,
  rayleighPhaseFn, cornetteShanksPhaseFn, rayShellIntersect,
  createSkyUniforms, updateSkyUniforms, type SkyUniforms,
} from './shaders/atmosphereFn.js';
import {
  SkyLutSet, type SkyLutUpdateResult,
  createSkyViewNodeUniforms, buildSkyViewBackgroundNode,
  type SkyViewNodeUniforms,
  transmittanceToTopOfAtmosphere,
  bakeTransmittanceLut, bakeMultiScatterLut, sampleMultiScatterLut,
} from './lut/index.js';
import { overcastRadianceNode, sunDiscNode, nightBakeRadianceNode } from './lut/skyViewNode.js';
import {
  computeDensities, rayleighPhase, cornetteShanksPhase,
} from './AtmosphereConfig.js';

/**
 * What the PMREM bake renders instead of the visible sky (2026-09-25, Atlas
 * realism §8 R2): the environment carries the weather (a heavier overcast
 * blend than the visible dome, which only greys in rain) and never the sun
 * DISC (a 64² cube cannot hold a 0.27° disc: it aliases into a random texel —
 * the DirectionalLight already carries the sun). Each field replaces the
 * visible value for the duration of one face render.
 */
export interface SkyBakeOverrides {
  radianceScale: number;
  overcastMix: number;
  overcastZenith: { x: number; y: number; z: number };
  overcastGround: { x: number; y: number; z: number };
  /**
   * Opaque caller data carried from the latch to `lastBakedOverrides`
   * (2026-09-26): Atlas stamps the sky's diffuse illuminance the cube was baked
   * under, so its `environmentIntensity` between bakes follows the sky's own
   * change, not only the exposure's (a sunset cube held ×64 at midnight).
   */
  tag?: number;
  /**
   * The sky's SHAPE the cube was baked under (2026-09-27, the latch law): the sun's elevation sine and the overcast
   * weight. The scalar ratio a caller carries between bakes cannot turn a sunset or storm cube into a clear-noon one
   * — a caller compares these to decide when the cube is the wrong regime and must be re-baked at once. Opaque here.
   */
  sunY?: number;
  cloud?: number;
  /**
   * THE BAKED NIGHT (2026-10-06): the night sky the CUBE carries (the LUT / march carry the sun's light only — a night
   * cube was black, so no shiny surface reflected the night sky). Absent = none; see SkyViewNodeUniforms.nightZenith.
   */
  night?: SkyBakeNight;
}

/** THE BAKED NIGHT's values (node output units — the caller's scene radiance). */
export interface SkyBakeNight {
  zenith: { x: number; y: number; z: number };
  horizon: { x: number; y: number; z: number };
  ground: { x: number; y: number; z: number };
  /** The strongest light dome: bearing (unit XZ), half-width and height (radians), its horizon excess radiance. */
  dome: { dirX: number; dirZ: number; halfWidth: number; height: number; color: { x: number; y: number; z: number } };
}

/** Which sky evaluation path to use. */
export type SkyPipeline = 'inline' | 'lut';

export interface SkyMarchParams {
  /** Primary view-ray steps. 20..40 works well. */
  numSamples?: number;
  /** Sun-visibility ray steps per main sample. 6..12 works well. */
  sunSamples?: number;
}

export interface SkyRendererOptions {
  atmosphere: AtmosphereConfig;
  march?: SkyMarchParams;
  /** Sun illuminance (cd/m² scale) applied as a constant multiplier. */
  sunIlluminance?: number;
  /**
   * Sky evaluation pipeline. 'inline' = v1 per-fragment ray-march (default,
   * zero-risk). 'lut' = v2 Hillaire 4-LUT path (samples the baked sky-view
   * LUT — ~10× cheaper per fragment, requires a CPU bake on sun/atm change).
   */
  pipeline?: SkyPipeline;
  /** Bake the aerial-perspective volume (compositing deferred). Default false. */
  enableAerialPerspective?: boolean;
  /**
   * The aerial volume's frame from the FIRST bake (SkyLutSet's AerialFrame; default 'screen'). An app that composites
   * the 'world' frame passes it here so boot does not bake a 'screen' volume first and then re-bake it the moment the
   * post chain switches the frame (shader sweep 2026-10-06).
   */
  aerialFrame?: 'screen' | 'world';
  /**
   * THE INLINE LUT PARITY (2026-09-26, Atlas realism session 3 — the WebGL2
   * horizon band). The v1 march is SINGLE scattering with no sun disc; the v2
   * LUT dome adds Hillaire's multiple-scattering term σ_s·Ψ_ms and an analytic
   * disc. Measured on the CPU (`inlineSkyRadianceCpu`, this march's mirror,
   * against `evaluateSkyViewRadiance` — the LUT maths the WebGPU dome and
   * Atlas's sampler share): the LUT dome is 4.28× the inline one on the 1°
   * horizon ring at a 48° sun, 3.2–4.3× over the whole dome by day and 10–93×
   * in the −5° twilight. Atlas's fog colour is that sampler's horizon ring, so
   * on WebGL2 the fully fogged far tier met a dome four times darker — the
   * bright band. With `lutParity` the march adds σ_s·Ψ_ms per sample from the
   * SAME CPU multi-scatter bake (a 64×16 transmittance under the canonical
   * 32² Ψ_ms, within 1.6 % of the LUT path's own) uploaded as a HalfFloat
   * texture (linearly filterable on WebGL2 without OES_texture_float_linear),
   * and draws the LUT node's disc (`sunDiscNode`); the mirror then matches the
   * LUT maths to 0.97–1.00 at every elevation and sun height. Inline pipeline
   * only; default false keeps the old single-scatter dome for every other
   * caller.
   */
  lutParity?: boolean;
}

/** Ψ_ms LUT side for the inline parity (the canonical Hillaire size). */
export const INLINE_MS_LUT_SIZE = 32;

/**
 * CPU mirror of the inline march (buildBackgroundNode's inline path) for one
 * view direction: µ = V·up, µs = L·up, cosVL = V·L, the camera radius `r0`
 * (km). `ms` adds the parity's σ_s·Ψ_ms term from a baked multi-scatter LUT.
 * Returns radiance per unit sunIlluminance (radianceScale 1, no overcast, no
 * disc). The harness / tests compare it with `evaluateSkyViewRadiance`.
 */
export function inlineSkyRadianceCpu(
  cfg: AtmosphereConfig, r0: number, mu: number, muS: number, cosVL: number,
  ms?: { data: Float32Array; size: number } | null, numSamples = 30, sunSamples = 8,
): { x: number; y: number; z: number } {
  const shell = (r: number, m: number): number => {
    const b = r * m, c = r * r - cfg.topRadius * cfg.topRadius;
    return -b + Math.sqrt(Math.max(0, b * b - c));
  };
  const rayLen = Math.max(shell(r0, mu), 0.01);
  const step = rayLen / numSamples;
  const pR = rayleighPhase(cosVL), pM = cornetteShanksPhase(cosVL, cfg.miePhaseG);
  const rs = cfg.rayleighScattering, msc = cfg.mieScattering, me = cfg.mieExtinction, oz = cfg.ozoneExtinction;
  const ext = (h: number): [number, number, number, number, number] => {
    const d = computeDensities(cfg, Math.max(h, -1));
    return [rs.x * d.rayleigh + me.x * d.mie + oz.x * d.ozone, rs.y * d.rayleigh + me.y * d.mie + oz.y * d.ozone,
      rs.z * d.rayleigh + me.z * d.mie + oz.z * d.ozone, d.rayleigh, d.mie];
  };
  let ax = 0, ay = 0, az = 0, tx = 1, ty = 1, tz = 1;
  for (let i = 0; i < numSamples; i++) {
    const t = step * (i + 0.5);
    const r = Math.sqrt(r0 * r0 + t * t + 2 * r0 * mu * t);
    const alt = r - cfg.bottomRadius;
    const [ex, ey, ez, dR, dM] = ext(alt);
    // THE LOCAL SUN (2026-10-06): the sun's zenith cosine AT the sample, (r0·µs + t·ν)/r — never the camera's (that
    // flattened the planet: after sunset the air toward the sun was as dark as the air away from it). The planet's
    // shadow: a sample whose sun ray meets the ground gets no single scatter (the LUT's earthShadowCpu).
    const muSL = Math.min(1, Math.max(-1, (r0 * muS + t * cosVL) / r));
    const sb = r * muSL, sDisc = sb * sb - (r * r - cfg.bottomRadius * cfg.bottomRadius);
    const lit = sDisc >= 0 && Math.sqrt(sDisc) - sb > 0 ? 0 : 1;
    const msU = Math.min(1, Math.max(0, muSL * 0.5 + 0.5));
    const tS = shell(r, muSL), ss = Math.max(tS, 0) / sunSamples;
    let ox = 0, oy = 0, oz2 = 0;
    for (let j = 0; j < sunSamples; j++) {
      const tj = ss * (j + 0.5);
      const e = ext(Math.sqrt(r * r + tj * tj + 2 * r * muSL * tj) - cfg.bottomRadius);
      ox += e[0] * ss; oy += e[1] * ss; oz2 += e[2] * ss;
    }
    const sR = [rs.x * dR, rs.y * dR, rs.z * dR], sM = [msc.x * dM, msc.y * dM, msc.z * dM];
    let px = 0, py = 0, pz = 0;
    if (ms) {
      const v = Math.min(1, Math.max(0, alt / (cfg.topRadius - cfg.bottomRadius)));
      const psi = sampleMultiScatterLut(ms.data, ms.size, msU, v);
      px = psi.x; py = psi.y; pz = psi.z;
    }
    ax += ((sR[0] * pR + sM[0] * pM) * Math.exp(-ox) * lit + (sR[0] + sM[0]) * px) * tx * step;
    ay += ((sR[1] * pR + sM[1] * pM) * Math.exp(-oy) * lit + (sR[1] + sM[1]) * py) * ty * step;
    az += ((sR[2] * pR + sM[2] * pM) * Math.exp(-oz2) * lit + (sR[2] + sM[2]) * pz) * tz * step;
    tx *= Math.exp(-ex * step); ty *= Math.exp(-ey * step); tz *= Math.exp(-ez * step);
  }
  return { x: ax, y: ay, z: az };
}

/** Bake the parity's Ψ_ms (Float32 RGBA, INLINE_MS_LUT_SIZE²) from a small transmittance LUT. */
export function bakeInlineMultiScatter(cfg: AtmosphereConfig): Float32Array {
  const tt = bakeTransmittanceLut(cfg, 64, 16, 24);
  const ms = bakeMultiScatterLut({
    cfg, transmittanceData: tt.data, transmittanceWidth: tt.width, transmittanceHeight: tt.height,
  }, INLINE_MS_LUT_SIZE);
  // Never uploaded (only their data is read) — release the bakers' Float textures at once.
  tt.texture.dispose(); ms.texture.dispose();
  return ms.data;
}

/** Write Float32 RGBA into a HalfFloat DataTexture's Uint16 image (WebGL2 filters half floats in core). */
function writeHalfLut(tex: THREE.DataTexture, data: Float32Array): void {
  const out = tex.image.data as unknown as Uint16Array;
  for (let i = 0; i < data.length; i++) out[i] = THREE.DataUtils.toHalfFloat(data[i]);
  tex.needsUpdate = true;
}

/**
 * Holds the TSL node + mutable uniforms so the caller can push changes
 * (sun direction, atmosphere config, camera altitude) each frame without
 * rebuilding the node graph.
 */
export class SkyRenderer {
  readonly skyUniforms: SkyUniforms;
  readonly sunDirection: ReturnType<typeof uniform>;
  readonly sunIlluminance: ReturnType<typeof uniform>;
  readonly cameraAltitudeKm: ReturnType<typeof uniform>;
  readonly numSamples: number;
  readonly sunSamples: number;
  readonly pipeline: SkyPipeline;

  private _node: unknown | null = null;
  private _atmosphere: AtmosphereConfig;

  // ── v2 LUT path state (only populated when pipeline === 'lut') ──
  private _lutSet: SkyLutSet | null = null;
  private _lutNodeUniforms: SkyViewNodeUniforms | null = null;
  /** Current sun direction kept on the CPU for sun-zenith cosine derivation. */
  private _sunDir = new THREE.Vector3(0, 1, 0);
  /** Altitude the sun-disc tint was last integrated at (NaN = never). */
  private _sunTintAltitudeKm = Number.NaN;
  /**
   * The physical-light hooks shared by BOTH pipelines (see SkyViewNodeUniforms):
   * neutral defaults, so a caller that never writes them keeps the old sky.
   */
  readonly look = {
    radianceScale: uniform(1),
    discScale: uniform(1),
    overcastMix: uniform(0),
    overcastZenith: uniform(vec3(0, 0, 0)),
    overcastGround: uniform(vec3(0, 0, 0)),
    // THE BAKED NIGHT: zero except while a cube face renders (beginBake / endBake).
    nightZenith: uniform(vec3(0, 0, 0)),
    nightHorizon: uniform(vec3(0, 0, 0)),
    nightGround: uniform(vec3(0, 0, 0)),
    nightDome: uniform(vec4(1, 0, 0.5, 0.1)),
    nightDomeColor: uniform(vec3(0, 0, 0)),
  };
  /** The inline LUT parity's Ψ_ms texture + sun-disc uniforms (null / unused without `lutParity`). */
  private _inlineMs: THREE.DataTexture | null = null;
  readonly inlineSunTint = uniform(vec3(1, 1, 1));
  readonly inlineSunAngularRadius = uniform(0.0047);
  private _bakeOverrides: SkyBakeOverrides | null = null;
  private _latched: SkyBakeOverrides | null = null;
  private _lastBaked: SkyBakeOverrides | null = null;
  private _saved: { rs: number; ds: number; om: number; oz: THREE.Vector3; og: THREE.Vector3 } | null = null;

  constructor(opts: SkyRendererOptions) {
    this.numSamples = opts.march?.numSamples ?? 30;
    this.sunSamples = opts.march?.sunSamples ?? 8;
    this.pipeline = opts.pipeline ?? 'inline';
    this._atmosphere = opts.atmosphere;

    this.skyUniforms = createSkyUniforms(opts.atmosphere);
    this.sunDirection = uniform(vec3(0, 1, 0));      // default: sun at zenith
    this.sunIlluminance = uniform(opts.sunIlluminance ?? 20.0); // unitless — user-tuned
    this.cameraAltitudeKm = uniform(opts.atmosphere.bottomRadius + 0.001);

    if (this.pipeline === 'lut') {
      this._lutSet = new SkyLutSet({
        atmosphere: opts.atmosphere,
        sunIlluminance: opts.sunIlluminance ?? 20.0,
        enableAerialPerspective: opts.enableAerialPerspective ?? false,
        aerialFrame: opts.aerialFrame,
      });
      this._lutNodeUniforms = createSkyViewNodeUniforms({
        sunIlluminance: opts.sunIlluminance ?? 20.0,
        bottomRadius: opts.atmosphere.bottomRadius,
        viewHeightKm: opts.atmosphere.bottomRadius + 0.001,
      });
      // One set of look uniforms for both nodes (the bake swaps them in place).
      Object.assign(this._lutNodeUniforms, this.look);
    }

    if (this.pipeline === 'inline' && opts.lutParity) {
      const data = bakeInlineMultiScatter(opts.atmosphere);
      const n = INLINE_MS_LUT_SIZE;
      const tex = new THREE.DataTexture(new Uint16Array(n * n * 4), n, n, THREE.RGBAFormat, THREE.HalfFloatType);
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.colorSpace = THREE.NoColorSpace;
      tex.generateMipmaps = false;
      tex.name = 'SkyInlineMultiScatterLUT';
      writeHalfLut(tex, data);
      this._inlineMs = tex;
    }
  }

  /** The inline LUT parity's Ψ_ms texture (null unless `lutParity` on the inline pipeline). */
  get inlineMultiScatter(): THREE.DataTexture | null { return this._inlineMs; }

  /** Whole-sky radiance multiplier (LUT + disc; the inline march too). */
  setRadianceScale(v: number): void { this.look.radianceScale.value = Math.max(0, v); }
  /** Sun-disc radiance multiplier (on top of sunIlluminance · radianceScale). */
  setDiscScale(v: number): void { this.look.discScale.value = Math.max(0, v); }
  /** Blend the dome toward a CIE overcast gradient (radiances in output units). */
  setOvercast(mix: number, zenith: { x: number; y: number; z: number }, ground: { x: number; y: number; z: number }): void {
    this.look.overcastMix.value = Math.max(0, Math.min(1, mix));
    (this.look.overcastZenith.value as THREE.Vector3).set(zenith.x, zenith.y, zenith.z);
    (this.look.overcastGround.value as THREE.Vector3).set(ground.x, ground.y, ground.z);
  }
  /** What the environment bake should render (null = the visible sky, disc and all — the old behaviour). */
  setBakeOverrides(o: SkyBakeOverrides | null): void {
    this._bakeOverrides = o ? {
      ...o, overcastZenith: { ...o.overcastZenith }, overcastGround: { ...o.overcastGround },
      night: o.night ? { zenith: { ...o.night.zenith }, horizon: { ...o.night.horizon }, ground: { ...o.night.ground }, dome: { ...o.night.dome, color: { ...o.night.dome.color } } } : undefined,
    } : null;
  }
  /** The baker calls this at the START of a cycle: every face of one cube renders with the same values. */
  latchBakeOverrides(): void { this._latched = this._bakeOverrides ? { ...this._bakeOverrides } : null; }
  /** Swap the bake values in for one face render (paired with endBake). */
  beginBake(): void {
    const o = this._latched;
    if (!o || this._saved) return;
    const L = this.look;
    this._saved = {
      rs: L.radianceScale.value as number, ds: L.discScale.value as number, om: L.overcastMix.value as number,
      oz: (L.overcastZenith.value as THREE.Vector3).clone(), og: (L.overcastGround.value as THREE.Vector3).clone(),
    };
    L.radianceScale.value = o.radianceScale; L.discScale.value = 0; L.overcastMix.value = o.overcastMix;
    (L.overcastZenith.value as THREE.Vector3).set(o.overcastZenith.x, o.overcastZenith.y, o.overcastZenith.z);
    (L.overcastGround.value as THREE.Vector3).set(o.overcastGround.x, o.overcastGround.y, o.overcastGround.z);
    // THE BAKED NIGHT: on for the face render only (the visible dome keeps its zeros — endBake restores them).
    const n = o.night;
    (L.nightZenith.value as THREE.Vector3).set(n?.zenith.x ?? 0, n?.zenith.y ?? 0, n?.zenith.z ?? 0);
    (L.nightHorizon.value as THREE.Vector3).set(n?.horizon.x ?? 0, n?.horizon.y ?? 0, n?.horizon.z ?? 0);
    (L.nightGround.value as THREE.Vector3).set(n?.ground.x ?? 0, n?.ground.y ?? 0, n?.ground.z ?? 0);
    (L.nightDome.value as THREE.Vector4).set(n?.dome.dirX ?? 1, n?.dome.dirZ ?? 0, n?.dome.halfWidth ?? 0.5, n?.dome.height ?? 0.1);
    (L.nightDomeColor.value as THREE.Vector3).set(n?.dome.color.x ?? 0, n?.dome.color.y ?? 0, n?.dome.color.z ?? 0);
  }
  endBake(): void {
    const sv = this._saved;
    if (!sv) return;
    const L = this.look;
    L.radianceScale.value = sv.rs; L.discScale.value = sv.ds; L.overcastMix.value = sv.om;
    (L.overcastZenith.value as THREE.Vector3).copy(sv.oz); (L.overcastGround.value as THREE.Vector3).copy(sv.og);
    (L.nightZenith.value as THREE.Vector3).set(0, 0, 0); (L.nightHorizon.value as THREE.Vector3).set(0, 0, 0);
    (L.nightGround.value as THREE.Vector3).set(0, 0, 0); (L.nightDomeColor.value as THREE.Vector3).set(0, 0, 0);
    this._saved = null;
  }
  /** The baker calls this when a cube completes: `lastBakedOverrides` is what the live environment holds. */
  bakeCompleted(): void { this._lastBaked = this._latched ? { ...this._latched } : null; this._bakeSeq++; }
  get lastBakedOverrides(): SkyBakeOverrides | null { return this._lastBaked; }
  /** The overrides the cycle IN FLIGHT latched at its first face (the last cycle's once it completed) — 2026-09-27. */
  get latchedBakeOverrides(): SkyBakeOverrides | null { return this._latched; }
  /** Completed bakes so far (a caller sees a new cube land by this moving). */
  get bakeSeq(): number { return this._bakeSeq; }
  private _bakeSeq = 0;

  /** The owned LUT set (v2 path only; null in inline mode). */
  get lutSet(): SkyLutSet | null { return this._lutSet; }

  /** The atmosphere config currently driving this renderer. */
  get atmosphere(): AtmosphereConfig { return this._atmosphere; }

  /** Push new atmosphere parameters to the GPU. */
  setAtmosphere(cfg: AtmosphereConfig): void {
    this._atmosphere = cfg;
    // The sun-disc tint integrates THIS config: invalidate the early-out key so
    // the next (possibly identical) sun push re-integrates against the new one.
    this._sunTintAltitudeKm = NaN;
    updateSkyUniforms(this.skyUniforms, cfg);
    this._lutSet?.setAtmosphere(cfg);
    if (this._inlineMs) writeHalfLut(this._inlineMs, bakeInlineMultiScatter(cfg)); // in place: the node keeps its texture
    if (this._lutNodeUniforms) {
      this._lutNodeUniforms.bottomRadius.value = cfg.bottomRadius;
    }
  }

  /** Set the world-space unit sun direction (FROM origin TO sun). */
  setSunDirection(dir: { x: number; y: number; z: number }): void {
    const altKm = this.cameraAltitudeKm.value as number;
    // SkyRuntimeSystem (and every cloud layer) pushes the sun EVERY frame; when
    // neither the direction nor the altitude the tint was integrated at has
    // changed, every write below is a no-op — skip the ~40-sample integral.
    const unchanged = this._sunDir.x === dir.x && this._sunDir.y === dir.y
      && this._sunDir.z === dir.z && this._sunTintAltitudeKm === altKm;
    if (unchanged) return;
    (this.sunDirection.value as THREE.Vector3).set(dir.x, dir.y, dir.z);
    this._sunDir.set(dir.x, dir.y, dir.z);
    if (this._lutNodeUniforms || this._inlineMs) {
      // Sun-disc tint = atmospheric transmittance toward the sun, evaluated on
      // the CPU from the SAME config the LUTs bake from (~40-sample integral
      // — only when the sun or the observer altitude actually moved). White
      // at noon, deep orange at the horizon, → 0 below it. The disc previously
      // rendered flat white at every elevation — the biggest sunset realism miss.
      const cosZenith = Math.max(-1, Math.min(1, dir.y));
      const t = transmittanceToTopOfAtmosphere(
        this._atmosphere,
        altKm,
        cosZenith,
      );
      if (this._lutNodeUniforms) {
        (this._lutNodeUniforms.sunDirection.value as THREE.Vector3).set(dir.x, dir.y, dir.z);
        (this._lutNodeUniforms.sunTint.value as THREE.Vector3).set(t.x, t.y, t.z);
      }
      if (this._inlineMs) (this.inlineSunTint.value as THREE.Vector3).set(t.x, t.y, t.z);
    }
    this._sunTintAltitudeKm = altKm;
  }

  /** Set the camera altitude in kilometres from planet center. */
  setCameraAltitudeKm(radiusKm: number): void {
    this.cameraAltitudeKm.value = radiusKm;
    if (this._lutNodeUniforms) {
      this._lutNodeUniforms.viewHeightKm.value = radiusKm;
    }
  }

  /**
   * THE AERIAL ALTITUDE (2026-10-06): the aerial froxel volume's own observer radius (km), when the sky-view's observer
   * is held at the ground (SkyLutSet.setAerialViewHeightKm). null = follow `cameraAltitudeKm`. LUT pipeline only.
   */
  setAerialAltitudeKm(radiusKm: number | null): void { this._lutSet?.setAerialViewHeightKm(radiusKm); }

  /** Set the sun illuminance multiplier. */
  setSunIlluminance(v: number): void {
    this.sunIlluminance.value = v;
    this._lutSet?.setSunIlluminance(v);
    if (this._lutNodeUniforms) {
      this._lutNodeUniforms.sunIlluminance.value = v;
    }
  }

  /**
   * v2 path: regenerate dirty LUTs from the current sun + camera altitude.
   * No-op for the inline pipeline. Returns which tiers re-baked (so the
   * caller can decide whether to trigger a PMREM env re-bake), or null when
   * not in LUT mode.
   *
   * The sun-zenith cosine is derived from the world-space sun direction's
   * Y component (world +Y is "up" in the flat-frame scene, matching the
   * inline node's `up = vec3(0,1,0)` convention).
   */
  updateLuts(force = false): SkyLutUpdateResult | null {
    if (!this._lutSet) return null;
    const cosSunZenith = Math.max(-1, Math.min(1, this._sunDir.y));
    const viewHeightKm = this.cameraAltitudeKm.value as number;
    return this._lutSet.update(cosSunZenith, viewHeightKm, force);
  }

  /**
   * Build (and memoize) the TSL node that evaluates sky radiance given a
   * world-space view direction. Intended to be assigned to
   * `scene.backgroundNode` or used inside a custom skybox material.
   *
   * The view ray is `positionWorldDirection` — the fragment's direction on
   * the sky dome / background sphere, independent of camera translation
   * (a background is at infinity). Altitude comes from the
   * `cameraAltitudeKm` uniform, never from the camera's world position.
   */
  buildBackgroundNode(): unknown {
    if (this._node) return this._node;

    // ── v2 LUT path: sample the baked sky-view LUT + analytic sun disc. ──
    if (this.pipeline === 'lut' && this._lutSet && this._lutNodeUniforms) {
      // Ensure the LUTs exist before the node references the sky-view texture.
      this._lutSet.update(
        Math.max(-1, Math.min(1, this._sunDir.y)),
        this.cameraAltitudeKm.value as number,
        true,
      );
      const sv = this._lutSet.skyView;
      if (sv) {
        this._node = buildSkyViewBackgroundNode(sv.texture, this._lutNodeUniforms);
        return this._node;
      }
      // Fall through to inline if the bake somehow failed.
    }

    const u = this.skyUniforms;
    const numSamples = this.numSamples;
    const sunSamples = this.sunSamples;
    const msTex = this._inlineMs;

    // View direction for a fragment on the background sphere.
    // WHY (the "sky slides when the player moves" bug): scene.backgroundNode is
    // drawn on a UNIT sphere with an IDENTITY matrixWorld, camera-locked via a
    // w=0 vertex transform (three common/Background.js) — so positionWorld here
    // is a unit-length direction, NOT cameraPos + dir. The old
    // normalize(positionWorld - cameraPosition) subtracted a metres-scale camera
    // position from a unit vector, swinging every sky ray as the camera
    // translated (sky black/uniform away from the origin, and the night sky
    // visibly slid against the star field while walking). A star is at
    // infinity: its ray must be translation-invariant — positionWorldDirection
    // is exactly that (and identical on the env-baker's origin-centred sphere).
    const viewDirWorld = positionWorldDirection;

    // Approximate "up" = world +Y. The planet's "up" at the camera is
    // straight up in world space for a flat-earth local scene.
    const up = vec3(0, 1, 0);

    // Camera radius from planet center (km) — user-set uniform.
    const r0 = this.cameraAltitudeKm;

    // Scattering evaluation — inline ray-march.
    const evalSkyFn = Fn(() => {
      const V = viewDirWorld;
      const L = normalize(this.sunDirection);
      const mu = dot(V, up);                 // cosViewZenith
      const muS = dot(L, up);                // cosSunZenith (at camera)
      const cosVL = dot(V, L);               // scatter angle cosine

      // Ray length through the atmosphere shell
      const tMax = rayShellIntersect([r0, mu, u.topRadius]);
      // Guard tiny values so divisions don't blow up
      const rayLen = max(tMax, float(0.01));

      const stepLen = rayLen.div(float(numSamples));
      const phaseR = rayleighPhaseFn([cosVL]);
      const phaseM = cornetteShanksPhaseFn([cosVL, u.miePhaseG]);

      // Accumulators
      const accumScatter = vec3(0, 0, 0).toVar('accumScatter');
      const accumTransmittance = vec3(1, 1, 1).toVar('accumTransmittance');

      // Main view-ray march.
      Loop({ start: uint(0), end: uint(numSamples), type: 'uint' }, ({ i }) => {
        const tMid = stepLen.mul(float(i).add(0.5));
        // Altitude at this sample, approximated linearly along the ray
        // (exact would use sqrt(r0² + 2*r0*mu*t + t²) - Rg).
        const rAtSample = sqrt(
          r0.mul(r0).add(tMid.mul(tMid)).add(r0.mul(mu).mul(tMid).mul(2.0)),
        );
        const altitudeKm = rAtSample.sub(u.bottomRadius);

        const density = atmosphereDensities([altitudeKm, u]);
        const stepExtinction = u.rayleighScattering.mul(density.x)
          .add(u.mieExtinction.mul(density.y))
          .add(u.ozoneExtinction.mul(density.z));
        const stepTransmittance = exp(stepExtinction.mul(stepLen).negate());

        // Sun-visibility ray from this sample to top of atm along -L direction.
        // Assume Sun direction — the ray from sample → top in -L direction
        // is: travel distance `tSun` within the shell. Compute µ_L at sample
        // (dot of L with local up at sample).
        // THE LOCAL SUN (2026-10-06): the sun's zenith cosine AT the sample, (r0·µs + t·ν)/r — the flat µs flattened the
        // planet (after sunset the air toward the sun was as dark as the air away: no twilight arch, no Earth's shadow).
        const muSunAtSample = r0.mul(muS).add(tMid.mul(cosVL)).div(rAtSample).clamp(-1, 1);
        // The planet's shadow: a sample whose sun ray meets the ground (the near root ahead) gets no single scatter.
        const shadowB = rAtSample.mul(muSunAtSample);
        const shadowDisc = shadowB.mul(shadowB).sub(rAtSample.mul(rAtSample).sub(u.bottomRadius.mul(u.bottomRadius)));
        const sunLit = float(1).sub(step(float(0), shadowDisc).mul(float(1).sub(step(sqrt(max(shadowDisc, float(0))).sub(shadowB), float(0)))));
        const msU = muSunAtSample.mul(0.5).add(0.5).clamp(0, 1);
        const tSun = rayShellIntersect([rAtSample, muSunAtSample, u.topRadius]);
        const sunStep = max(tSun, float(0)).div(float(sunSamples));
        const sunOpticalDepth = vec3(0, 0, 0).toVar('sunOpticalDepth');
        Loop({ start: uint(0), end: uint(sunSamples), type: 'uint' }, ({ i: j }) => {
          const tSunMid = sunStep.mul(float(j).add(0.5));
          const rSun = sqrt(
            rAtSample.mul(rAtSample)
              .add(tSunMid.mul(tSunMid))
              .add(rAtSample.mul(muSunAtSample).mul(tSunMid).mul(2.0)),
          );
          const altSun = rSun.sub(u.bottomRadius);
          const dSun = atmosphereDensities([altSun, u]);
          const extSun = u.rayleighScattering.mul(dSun.x)
            .add(u.mieExtinction.mul(dSun.y))
            .add(u.ozoneExtinction.mul(dSun.z));
          sunOpticalDepth.addAssign(extSun.mul(sunStep));
        });
        const sunTransmittance = exp(sunOpticalDepth.negate());

        // In-scattering at this sample
        const scatterR = u.rayleighScattering.mul(density.x).mul(phaseR);
        const scatterM = u.mieScattering.mul(density.y).mul(phaseM);
        const single = scatterR.add(scatterM).mul(sunTransmittance).mul(sunLit);
        // LUT parity: + σ_s·Ψ_ms (isotropic — no phase, no sun transmittance: Ψ_ms already integrates them).
        const inScatter = msTex
          ? single.add(u.rayleighScattering.mul(density.x).add(u.mieScattering.mul(density.y))
            .mul(texture(msTex, vec2(msU, altitudeKm.div(u.topRadius.sub(u.bottomRadius)).clamp(0, 1))).level(float(0)).xyz))
          : single;
        accumScatter.addAssign(
          inScatter.mul(accumTransmittance).mul(stepLen),
        );
        accumTransmittance.mulAssign(stepTransmittance);
      });

      const final = accumScatter.mul(this.sunIlluminance).mul(this.look.radianceScale);
      // The overcast blend (the physical-light hooks; neutral at mix 0).
      const oc = vec3(overcastRadianceNode(mu, this.look.overcastZenith, this.look.overcastGround) as never);
      // THE BAKED NIGHT: zero outside a bake (beginBake swaps it in).
      const night = vec3(nightBakeRadianceNode(V, this.look.nightZenith, this.look.nightHorizon, this.look.nightGround, this.look.nightDome, this.look.nightDomeColor) as never);
      const sky = mix(final, oc, this.look.overcastMix).add(night);
      if (!msTex) return vec4(sky, 1.0);
      // LUT parity: the LUT node's disc, scaled exactly as there.
      const disc = vec3(sunDiscNode(V, L, this.inlineSunAngularRadius, this.inlineSunTint) as never)
        .mul(this.sunIlluminance).mul(this.look.discScale).mul(this.look.radianceScale)
        .mul(float(1).sub(this.look.overcastMix));
      return vec4(sky.add(disc), 1.0);
    });

    this._node = evalSkyFn();
    return this._node;
  }

  /**
   * Returns a ready-to-use THREE.NodeMaterial that evaluates the sky for
   * any fragment. Useful when rendering the sky into a cube target or a
   * sphere mesh (rather than via scene.backgroundNode).
   */
  buildSkyMaterial(): THREE.NodeMaterial {
    const material = new THREE.NodeMaterial();
    material.fragmentNode = this.buildBackgroundNode() as never;
    material.side = THREE.BackSide;
    material.depthWrite = false;
    material.depthTest = false;
    return material;
  }

  dispose(): void {
    this._node = null;
    this._inlineMs?.dispose();
    this._inlineMs = null;
    this._lutSet?.dispose();
    this._lutSet = null;
    this._lutNodeUniforms = null;
  }
}
